import React, { useContext, useEffect, useState } from 'react';
import { IRootState } from 'store/serverInterfaces';
import { RouteComponentProps } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { IGame } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';

import Header, { GameHeaderContent } from 'components/Header';
import Config from 'theme/config';
import FivesView, { IFivesViewData } from './FivesView';
import GamesSlider from '../../GamesSlider';
import BonusWheel from 'components/BonusWheel';
import { IInitialState } from 'interfaces/gameInterfaces';
import { getPrize, setHidePrize, setPrizeSuccess } from 'store/actions/dreamteamActions';
import Loading from 'components/Loading';
import { getEventsByTeam } from 'store/actions/sportsActions';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import { ReactGACategories } from 'constants/appEnums';
import usePrevious from 'hooks/usePrevious';
import RestrictedContainer from 'components/RestrictedContainer';
import UpsellBanner from '../../UpsellBanner';

export interface IFivesProps extends RouteComponentProps {}

const Fives = (props: IFivesProps) => {
  const dreamteamReducer: IInitialState = useSelector((state: IRootState) => state.DreamteamReducer);
  const dispatch = useDispatch();
  const [showBonusWheel, setShowBonusWheel] = useState<boolean>(false);
  const userContext = useContext(UserContext);
  const previousRevealAnimationInProgressState = usePrevious(dreamteamReducer?.revealAnimationInProgress || false);

  // IF the token is invalid we reset the app with no token
  useEffect(() => {
    if (dreamteamReducer.error === 'invalid-exttoken') {
      userContext.setPlayerData('');
    }
  }, [dreamteamReducer.error, props.history, userContext]);

  useEffect(() => {
    dreamteamUtilities.redirectIfNotAuthenticated(userContext.user.isAuthenticated, dispatch, props.history);

    if (userContext.user.token && dreamteamReducer.selectedEntry && dreamteamUtilities.isEntryFinished(dreamteamReducer.selectedEntry)) {
      dispatch(getPrize(userContext.user.token, dreamteamReducer.selectedEntry.receipt));
    } else {
      dispatch(setPrizeSuccess(undefined));
    }
  }, [dispatch, props.history, userContext.user, dreamteamReducer.selectedEntry]);

  useEffect(() => {
    if (!dreamteamReducer.revealAnimationInProgress) {
      const fireAnimation = setTimeout(() => {
        if (
          previousRevealAnimationInProgressState &&
          !dreamteamReducer.revealAnimationInProgress &&
          dreamteamReducer.selectedEntry &&
          dreamteamUtilities.isEntryFinished(dreamteamReducer.selectedEntry)
        ) {
          setShowBonusWheel(true);
        }
      }, 500);
      return () => {
        clearTimeout(fireAnimation);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dreamteamReducer.revealAnimationInProgress]);

  useEffect(() => {
    if (dreamteamReducer.selectedEntry) {
      dispatch(getEventsByTeam(dreamteamReducer.selectedEntry.team));
    }
  }, [dispatch, dreamteamReducer.selectedEntry]);

  const wheelCallback = () => {
    setShowBonusWheel(false);
    dispatch(setHidePrize(false));
    if (dreamteamReducer.prize) {
      dreamteamUtilities.recordEventGA(ReactGACategories.entry, 'Reveal Prize');
    } else {
      dreamteamUtilities.recordEventGA(ReactGACategories.entry, 'Failure', 'On reveal prize, the wheel fired but there is not prize');
    }
  };

  const fivesData: IFivesViewData = {
    entry: dreamteamReducer.selectedEntry,
    game: dreamteamReducer.games?.find((game: IGame) => game.gameId === dreamteamReducer.selectedGameId),
    error: dreamteamReducer.error && !showBonusWheel // Don't show error while bonusWheel triggered
  };

  // Attempted to get currency code
  const currencyCode = dreamteamReducer?.selectedEntry?.currencyCode || dreamteamReducer?.prize?.prizeCurrencyCode || 'GBP';
  // Set default prize to lowest avaible value
  const prizeArray = Config.bonusWheel.prizesConfig[currencyCode]?.prizeArray;
  const defaultPrizeValue = prizeArray ? Math.min(...prizeArray.map((prize: { value: number; bgColor: string; textColor: string }) => prize.value)) : 0;

  const multiplier = dreamteamReducer?.prize?.prizeMultiplier || 1;
  const prizeAmount = dreamteamReducer?.prize?.prizeAmount || defaultPrizeValue;

  return (
    <>
      <GamesSlider />
      <RestrictedContainer>
        <Header>
          <GameHeaderContent />
        </Header>
        {dreamteamReducer.loadingDataForGame ? (
          <Loading showText={true} />
        ) : (
          <>
            <FivesView data={fivesData} />
            {showBonusWheel && <BonusWheel prizeMultiplier={multiplier} prizeAmount={prizeAmount} onFinishedCallback={wheelCallback} prizeCurrencyCode={currencyCode} />}
          </>
        )}
        <UpsellBanner />
      </RestrictedContainer>
    </>
  );
};

export default Fives;
